import { useEffect } from "react";

import { pm2ConfigStore } from "@pm2/store";
import { useConsentSelector } from "@provider/consent";
import { useUserIdentityState } from "@provider/user-identity";

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    sleeknoteSiteData?: Array<Record<string, unknown>>;
  }
}

let hasInitializedSleeknote = false;

/**
 * setup Sleeknote marketing / tracking script
 *
 * lazy load the actual script so it doesn't interfere with initial site
 * rendering performance
 */
export function useSleeknote(): void {
  const enableSleeknote = pm2ConfigStore.useSelector(
    (state) => state.config.settings.enableSleeknote,
  );
  const user = useUserIdentityState((state) => state.user);
  const hasConsented = useConsentSelector((state) => state.cookies.statistics);

  useEffect(() => {
    if (!enableSleeknote || !hasConsented) {
      return;
    }

    if (user === undefined) {
      // if user is null, it means that login status has not been resolved yet,
      // so defer initialization of Sleeknote
      return;
    }

    if (hasInitializedSleeknote) {
      // due to the way that sleeknote has been implemented, we cannot ever
      // run initialization more than once
      return;
    }

    hasInitializedSleeknote = true;

    // commit user data to the sleeknoteSiteData array which the script will
    // then subsequently reference later on
    const isTeacher = user?.role === "teacher";
    const hasAccess = user?.isAuthorized === true;

    window.sleeknoteSiteData ??= [];
    window.sleeknoteSiteData.push({ isTeacher });
    window.sleeknoteSiteData.push({ hasAccess });

    // load the Sleeknote script
    const sleeknoteScriptTag = document.createElement("script");

    sleeknoteScriptTag.type = "text/javascript";
    sleeknoteScriptTag.charset = "utf-8";
    sleeknoteScriptTag.src =
      "//sleeknotecustomerscripts.sleeknote.com/23378.js";

    const s = document.getElementById("sleeknoteScript");

    s?.parentNode?.insertBefore(sleeknoteScriptTag, s);

    return () => {
      s?.remove();
    };
  }, [enableSleeknote, user, hasConsented]);
}
